import { useState, useCallback } from 'react';

interface CardData {
  card_number: string;
  card_holder_name: string;
  card_expiration_month: number;
  card_expiration_year: number;
  card_cvv: string;
}

interface UsePagarmeReturn {
  tokenizeCard: (cardData: CardData, publicKey: string) => Promise<string>;
  loading: boolean;
  error: string | null;
  isReady: boolean;
}

const PAGARME_API_URL = 'https://api.pagar.me/core/v5/tokens';

export function usePagarme(enabled: boolean = false): UsePagarmeReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isReady = enabled;

  const tokenizeCard = useCallback(
    async (cardData: CardData, publicKey: string): Promise<string> => {
      if (!publicKey) {
        throw new Error('Pagar.me public key not provided');
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${PAGARME_API_URL}?appId=${publicKey}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'card',
            card: {
              number: cardData.card_number.replace(/\s/g, ''),
              holder_name: cardData.card_holder_name,
              exp_month: cardData.card_expiration_month,
              exp_year: cardData.card_expiration_year,
              cvv: cardData.card_cvv,
            },
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('[usePagarme] API error:', errorData);

          let errorMessage = 'Card tokenization failed';

          if (errorData.errors && Array.isArray(errorData.errors)) {
            errorMessage = errorData.errors.map((e: any) => e.message).join(', ');
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }

          throw new Error(errorMessage);
        }

        const data = await response.json();
        const token = data.id;

        if (!token || !token.startsWith('token_')) {
          throw new Error('Invalid token returned by the provider');
        }

        /* The token used to be logged in full. `drop_console` strips it from
           the production build, but in dev and preview it showed to anyone who
           opened the inspector — and it is the credential that stands for the
           card. Not logged at all now. */
        setLoading(false);
        return token;
      } catch (err: any) {
        console.error('[usePagarme] Tokenization error:', err);

        const errorMessage = err.message || 'Card tokenization failed';

        setError(errorMessage);
        setLoading(false);
        throw new Error(errorMessage);
      }
    },
    []
  );

  return {
    tokenizeCard,
    loading,
    error,
    isReady,
  };
}
